import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Network, ShieldCheck, BadgeCheck, UserPlus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { api } from "../../lib/api";
import { useLang } from "../../i18n/LanguageContext";
import { OrgChart } from "./OrgChart";
import { RolesPanel } from "./RolesPanel";
import { JobTitlesPanel } from "./JobTitlesPanel";
import { UserAccessSheet } from "./UserAccessSheet";
import { AddUserPanel } from "./AddUserPanel";

export const OrgStructureDialog = ({ open, onOpenChange, onPreview, full = true }) => {
  const { t, dir } = useLang();
  const [ctx, setCtx] = useState(null);
  const [openUser, setOpenUser] = useState(null);
  const [tab, setTab] = useState(full ? "add" : "chart");
  const load = useCallback(() => api.orgStructure().then(setCtx).catch((e) => toast.error(e?.response?.data?.detail || "تعذّر التحميل")), []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const sheetUser = openUser && ctx?.users.find((u) => u.uid === openUser);
  const actx = ctx && ctx.viewer_role === "hr_manager" ? { ...ctx, roles: ctx.roles.filter((r) => !["manager", "hr_manager"].includes(r.id)) } : ctx;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[94vh] sm:w-[96vw] sm:rounded-2xl" data-testid="org-structure-dialog">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 pb-4 pt-12 sm:pe-12 sm:pt-4">
          <div>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold"><Network className="h-5 w-5 text-blue-600" /> {full ? "المستخدمون والهيكل والصلاحيات" : "الهيكل التنظيمي"}</DialogTitle>
            <DialogDescription className="mt-0.5 text-xs">{full ? "أضف المستخدم، ثم حدّد قسمه ومسماه ودوره. اضغط على أي شخص في الهيكل لتعديل صلاحياته." : "رتّب الأقسام والمسميات الوظيفية واسحب الموظفين لأقسامهم. الأدوار والصلاحيات يعدّلها مالك الشركة."}</DialogDescription>
          </div>
        </div>
        {!ctx ? <div className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <Tabs value={tab} onValueChange={setTab} dir={dir} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="mx-5 mt-4 flex w-auto max-w-[calc(100%-2.5rem)] justify-start self-start overflow-x-auto">
              {full && <TabsTrigger value="add" className="shrink-0 gap-1.5" data-testid="org-tab-add"><UserPlus className="h-4 w-4" /> إضافة مستخدم</TabsTrigger>}
              <TabsTrigger value="chart" className="shrink-0 gap-1.5" data-testid="org-tab-chart"><Network className="h-4 w-4" /> الأقسام والهيكل</TabsTrigger>
              <TabsTrigger value="titles" className="shrink-0 gap-1.5" data-testid="org-tab-titles"><BadgeCheck className="h-4 w-4" /> المسميات الوظيفية</TabsTrigger>
              {full && <TabsTrigger value="roles" className="shrink-0 gap-1.5" data-testid="org-tab-roles"><ShieldCheck className="h-4 w-4" /> الأدوار والصلاحيات</TabsTrigger>}
            </TabsList>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 [background-image:radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:18px_18px]">
              {full && <TabsContent value="add" className="mt-0"><AddUserPanel ctx={actx} reload={load} onDone={() => setTab("chart")} /></TabsContent>}
              <TabsContent value="chart" className="mt-0"><OrgChart ctx={ctx} onOpen={(u) => full && setOpenUser(u.uid)} reload={load} /></TabsContent>
              <TabsContent value="titles" className="mt-0"><JobTitlesPanel ctx={ctx} reload={load} /></TabsContent>
              {full && <TabsContent value="roles" className="mt-0"><RolesPanel ctx={ctx} t={t} reload={load} /></TabsContent>}
            </div>
          </Tabs>
        )}
        {ctx && full && <UserAccessSheet u={sheetUser} ctx={actx} t={t} onClose={() => setOpenUser(null)} reload={load}
          onPreview={(p) => { setOpenUser(null); onOpenChange(false); onPreview(p); }} />}
      </DialogContent>
    </Dialog>
  );
};
